// Guided-tour content for every page, keyed by the nav key used in App.js.
//
// Targets are functions that look the element up in the live DOM, so the pages
// themselves need no changes (except a few data-tour markers on the Officer
// page). A step whose target is missing is dropped before the tour starts when
// it is marked `optional`, or when it is a `form` step and the page has no
// "add" button (the user lacks permission).
//
// Texts are Khmer source strings; tr() records the English pair, which is
// merged into the i18n dictionary (see GUIDE_EN in i18n/dict.js).

export const GUIDE_EN = {};
const tr = (km, en) => { GUIDE_EN[km] = en; return km; };

// ---------- DOM helpers ----------
const rootEl = () => document.querySelector("[data-guide-root]");
const inRoot = (sel) => () => rootEl()?.querySelector(sel) || null;
const byAttr = (name) => () => document.querySelector(`[data-guide="${name}"]`);
const selectN = (n) => () => rootEl()?.querySelectorAll("select")[n] || null;

const searchBox = () => {
  const input = rootEl()?.querySelector(
    'input:not([type="checkbox"]):not([type="date"]):not([type="file"]):not([type="radio"]):not([type="number"])'
  );
  return input ? (input.parentElement || input) : null;
};

const buttonWith = (...needles) => () =>
  [...(rootEl()?.querySelectorAll("button") || [])]
    .find(b => needles.some(n => b.textContent.includes(n))) || null;

export const addButton = () =>
  [...(rootEl()?.querySelectorAll("button") || [])]
    .find(b => b.textContent.trim().startsWith("+")) || null;

const tableBox   = () => rootEl()?.querySelector("table")?.parentElement || null;
const pagingBox  = () => tableBox()?.nextElementSibling || null;
const actionCell = () =>
  rootEl()?.querySelector("tbody tr:first-child td:last-child:not([colspan])") ||
  rootEl()?.querySelector("thead th:last-child") || null;

export const modalEl = () => document.querySelector('div[style*="z-index: 1000"]');
const modalCard = () => modalEl()?.firstElementChild || null;
const modalSave = () => {
  const b = modalEl()?.querySelectorAll("button");
  return b && b.length ? b[b.length - 1] : null;
};

// ---------- reusable steps ----------
const stSearch = (km, en) => ({
  target: searchBox, optional: true,
  title: tr("ស្វែងរក", "Search"),
  text:  tr(km || "វាយពាក្យគន្លឹះដើម្បីស្វែងរកក្នុងបញ្ជី។", en || "Type a keyword to search the list."),
});
const stFilter = (n, titleKm, titleEn, km, en) => ({
  target: selectN(n), optional: true,
  title: tr(titleKm || "ត្រងទិន្នន័យ", titleEn || "Filter"),
  text:  tr(km || "ជ្រើសរើសតម្លៃដើម្បីបង្ហាញតែទិន្នន័យដែលអ្នកត្រូវការ។", en || "Pick a value to show only the records you need."),
});
const stAdd = (titleKm, titleEn, km, en, target = addButton) => ({
  target, optional: true,
  title: tr(titleKm, titleEn),
  text:  tr(km, en),
});
const stTable = (km, en) => ({
  target: tableBox, optional: true,
  title: tr("បញ្ជី", "List"),
  text:  tr(km, en),
});
const stActions = (km, en) => ({
  target: actionCell, optional: true,
  title: tr("សកម្មភាព", "Actions"),
  text:  tr(
    km || "ចុចរូបភ្នែកដើម្បីមើល រូបខ្មៅដៃដើម្បីកែប្រែ និងរូបធុងសំរាមដើម្បីលុប។ ប៊ូតុងដែលអ្នកគ្មានសិទ្ធិប្រើនឹងមិនបង្ហាញទេ។",
    en || "Click the eye to view, the pencil to edit and the bin to delete. Buttons you are not allowed to use are hidden."
  ),
});
const stPaging = () => ({
  target: pagingBox, optional: true,
  title: tr("ទំព័រ", "Pages"),
  text:  tr("ប្តូរទំព័រ ឬចំនួនជួរក្នុងមួយទំព័រនៅទីនេះ។", "Change the page or the number of rows per page here."),
});
const stForm = (km, en, open) => ({
  form: true, open, target: modalCard,
  title: tr("បំពេញទម្រង់", "Fill in the form"),
  text:  tr(km, en),
});
const stSave = () => ({
  form: true, target: modalSave,
  title: tr("រក្សាទុក", "Save"),
  text:  tr(
    "ប្រអប់ដែលមានសញ្ញា * ត្រូវតែបំពេញ។ ចុចរក្សាទុកដើម្បីបញ្ចប់ ឬបោះបង់ដើម្បីចាកចេញដោយមិនរក្សាទុក។",
    "Fields marked * are required. Click Save to finish, or Cancel to leave without saving."
  ),
});

// ---------- pages ----------
const settingsCrud = (formKm, formEn, tableKm, tableEn) => [
  stSearch(),
  stFilter(0),
  stAdd("បន្ថែមថ្មី", "Add new", "ចុចប៊ូតុងនេះដើម្បីបង្កើតទិន្នន័យថ្មី។ ប៊ូតុងនេះបង្ហាញតែអ្នកដែលមានសិទ្ធិប៉ុណ្ណោះ។",
        "Click this button to create a new record. It only appears for people who are allowed to."),
  stTable(tableKm, tableEn),
  stActions(),
  stPaging(),
  stForm(formKm, formEn),
  stSave(),
];

const STAFF = [
  { target: inRoot('[data-tour="officer-search"]'), title: tr("ស្វែងរកមន្ត្រី", "Search officers"),
    text: tr("វាយឈ្មោះ ឬលេខកូដមន្ត្រី ដើម្បីស្វែងរកក្នុងបញ្ជី។", "Type a name or officer code to find someone in the list.") },
  { target: inRoot('[data-tour="officer-status"]'), title: tr("ត្រងតាមស្ថានភាព", "Filter by status"),
    text: tr("ជ្រើសរើសស្ថានភាព ដូចជា កំពុងបម្រើការ ចូលនិវត្តន៍ ឬបានលាឈប់ ដើម្បីបង្ហាញតែមន្ត្រីក្នុងស្ថានភាពនោះ។",
             "Pick a status such as Active, Retired or Resigned to show only officers in that status.") },
  { target: inRoot('[data-tour="officer-dept"]'), title: tr("ត្រងតាមនាយកដ្ឋាន", "Filter by department"),
    text: tr("ជ្រើសរើសនាយកដ្ឋាន ដើម្បីបង្ហាញតែមន្ត្រីនៅនាយកដ្ឋាននោះ។", "Pick a department to show only the officers in it.") },
  { target: inRoot('[data-tour="officer-add"]'), optional: true, title: tr("បន្ថែមមន្ត្រីថ្មី", "Add a new officer"),
    text: tr("ចុចប៊ូតុងនេះដើម្បីបង្កើតព័ត៌មានមន្ត្រីថ្មី។ ប៊ូតុងនេះបង្ហាញតែអ្នកដែលមានសិទ្ធិបង្កើតប៉ុណ្ណោះ។",
             "Click this button to create a new officer record. It only appears for people allowed to create officers.") },
  { target: inRoot('[data-tour="officer-table"]'), title: tr("បញ្ជីមន្ត្រី", "Officer list"),
    text: tr("តារាងបង្ហាញលេខកូដ ឈ្មោះ តំណែង ការិយាល័យ នាយកដ្ឋាន និងស្ថានភាពរបស់មន្ត្រីម្នាក់ៗ។",
             "The table shows each officer's code, name, position, office, department and status.") },
  { target: actionCell, optional: true, title: tr("សកម្មភាព", "Actions"),
    text: tr("ចុចរូបភ្នែកដើម្បីមើលលម្អិត រូបខ្មៅដៃដើម្បីកែប្រែ និងរូបធុងសំរាមដើម្បីលុប។ ក្នុងផ្ទាំងលម្អិត អ្នកអាចផ្អាក ឬបើកដំណើរការមន្ត្រីបាន។",
             "Click the eye to view details, the pencil to edit and the bin to delete. In the details window you can also suspend or activate an officer.") },
  { form: true, target: inRoot('[data-tour="officer-form-code"]'), title: tr("លេខកូដមន្ត្រី", "Officer code"),
    text: tr("បញ្ចូលលេខកូដមន្ត្រីមិនឲ្យស្ទួនគ្នា ដោយប្រើតែអក្សរឡាតាំង លេខ និងសញ្ញា - ឬ _ ប៉ុណ្ណោះ។",
             "Enter a unique officer code using only Latin letters, digits, - or _.") },
  { form: true, target: inRoot('[data-tour="officer-form-work"]'), title: tr("ព័ត៌មានការងារ", "Work information"),
    text: tr("ជ្រើសរើសនាយកដ្ឋានជាមុនសិន បន្ទាប់មកជ្រើសរើសការិយាល័យ និងតំណែងក្នុងនាយកដ្ឋាននោះ។",
             "Choose the department first, then pick an office and a position in that department.") },
  stSave(),
];

const statGrid  = () => rootEl()?.querySelector('div[style*="repeat(5"]') || null;
const panelGrid = () => rootEl()?.querySelector('div[style*="0.75fr"]') || null;
const panel = (i, j) => () => {
  const col = panelGrid()?.children[i];
  return (j == null ? col : col?.children[j]) || null;
};

const DASHBOARD = [
  { title: tr("សូមស្វាគមន៍", "Welcome"),
    text: tr("នេះជាការណែនាំខ្លីៗអំពីផ្ទាំងគ្រប់គ្រង។ អ្នកអាចចុចរំលងនៅពេលណាក៏បាន ហើយអាចមើលម្តងទៀតតាមរយៈប៊ូតុង ? នៅផ្នែកខាងលើ។",
             "A short tour of the dashboard. Skip at any time, and replay it with the ? button at the top.") },
  { target: () => rootEl()?.parentElement?.previousElementSibling || null, title: tr("ម៉ឺនុយ", "Menu"),
    text: tr("ជ្រើសរើសម៉ូឌុលដែលអ្នកចង់ប្រើនៅទីនេះ។ ម៉ឺនុយដែលអ្នកគ្មានសិទ្ធិនឹងមិនបង្ហាញទេ។",
             "Choose the module you want here. Menus you are not allowed to use are hidden.") },
  { target: statGrid, optional: true, title: tr("ស្ថិតិសង្ខេប", "Key numbers"),
    text: tr("ប័ណ្ណទាំងនេះបង្ហាញចំនួនមន្ត្រីសរុប កិច្ចប្រជុំថ្ងៃនេះ ឯកសារ និងកិច្ចសន្យាជិតផុតកំណត់ និងការជូនដំណឹងមិនទាន់អាន។ ប័ណ្ណខ្លះអាចចុចដើម្បីចូលទៅទំព័រពាក់ព័ន្ធ។",
             "These cards show total officers, today's meetings, documents and contracts about to expire, and unread notifications. Some can be clicked to open the related page.") },
  { target: panel(0), optional: true, title: tr("កិច្ចប្រជុំ", "Meetings"),
    text: tr("បង្ហាញកិច្ចប្រជុំនៃថ្ងៃដែលបានជ្រើសរើស។ ប្រើព្រួញ ឬប្រអប់ថ្ងៃខែដើម្បីប្តូរថ្ងៃ ហើយចុចកិច្ចប្រជុំដើម្បីចូលទៅទំព័រកិច្ចប្រជុំ។",
             "Shows the meetings of the chosen day. Use the arrows or the date box to change the day, and click a meeting to open the Meetings page.") },
  { target: panel(1), optional: true, title: tr("ការប្រកាសថ្មីៗ", "Latest announcements"),
    text: tr("បង្ហាញការប្រកាសចុងក្រោយបង្អស់។ ចុចលើការប្រកាសដើម្បីចូលទៅមើលបន្ថែម។",
             "Shows the most recent announcements. Click one to see more.") },
  { target: panel(2, 0), optional: true, title: tr("សកម្មភាពរហ័ស", "Quick actions"),
    text: tr("ផ្លូវកាត់ទៅកាន់ទំព័រដែលប្រើញឹកញាប់ ដូចជា មន្ត្រី កិច្ចប្រជុំ ការអនុម័ត និងការប្រកាស។",
             "Shortcuts to the pages used most often: officers, meetings, approvals and announcements.") },
  { target: panel(2, 1), optional: true, title: tr("សង្ខេបស្ថានភាព", "Status summary"),
    text: tr("របារទាំងនេះបង្ហាញមន្ត្រី ឯកសារជិតផុត និងកិច្ចប្រជុំថ្ងៃនេះឲ្យមើលបានភ្លាមៗ។ ចុចដើម្បីចូលទៅទំព័រពាក់ព័ន្ធ។",
             "These bars show officers, expiring documents and today's meetings at a glance. Click one to open the related page.") },
  { target: byAttr("bell"), optional: true, title: tr("ការជូនដំណឹង", "Notifications"),
    text: tr("ចុចរូបកណ្ដឹងដើម្បីមើលការជូនដំណឹងថ្មីៗ ហើយសម្គាល់ថាបានអានរួច។",
             "Click the bell to see new notifications and mark them as read.") },
  { target: byAttr("user-menu"), optional: true, title: tr("គណនីរបស់អ្នក", "Your account"),
    text: tr("មើលប្រវត្តិរូប ប្តូរពាក្យសម្ងាត់ ឬចាកចេញពីប្រព័ន្ធនៅទីនេះ។",
             "View your profile, change your password or log out here.") },
  { target: byAttr("help"), title: tr("ជំនួយ", "Help"),
    text: tr("ចុចប៊ូតុង ? នៅគ្រប់ទំព័រ ដើម្បីមើលការណែនាំរបស់ទំព័រនោះម្តងទៀត។",
             "Click the ? button on any page to replay that page's guide.") },
];

const CONTRACT = [
  stSearch("វាយឈ្មោះ ឬលេខកូដមន្ត្រីកិច្ចសន្យា ដើម្បីស្វែងរក។", "Type a contract officer's name or code to search."),
  stFilter(0, "ត្រងតាមស្ថានភាព", "Filter by status",
           "ជ្រើសរើសស្ថានភាពដើម្បីបង្ហាញតែមន្ត្រីកិច្ចសន្យាក្នុងស្ថានភាពនោះ។", "Pick a status to show only contract officers in that status."),
  stFilter(1, "ត្រងតាមនាយកដ្ឋាន", "Filter by department",
           "ជ្រើសរើសនាយកដ្ឋានដើម្បីបង្ហាញតែមន្ត្រីកិច្ចសន្យានៅនាយកដ្ឋាននោះ។", "Pick a department to show only its contract officers."),
  stAdd("បន្ថែមមន្ត្រីកិច្ចសន្យា", "Add a contract officer",
        "ចុចប៊ូតុងនេះដើម្បីបង្កើតព័ត៌មានមន្ត្រីកិច្ចសន្យាថ្មី។", "Click this button to create a new contract officer record."),
  stTable("តារាងបង្ហាញព័ត៌មានមន្ត្រីកិច្ចសន្យា រួមទាំងស្លាកដែលបង្ហាញថាកិច្ចសន្យាជិតផុតកំណត់ឬនៅ។",
          "The table lists contract officers, with a badge showing whether the contract is close to expiring."),
  stActions(),
  stPaging(),
  stForm("ជ្រើសរើសនាយកដ្ឋានជាមុនសិន បន្ទាប់មកជ្រើសរើសការិយាល័យ។ ថ្ងៃចាប់ផ្តើម និងថ្ងៃបញ្ចប់កិច្ចសន្យាត្រូវតែបំពេញ។",
         "Choose the department first, then the office. The contract start and end dates are required."),
  stSave(),
];

const DOC_OFFICERS = [
  stSearch("វាយឈ្មោះ ឬលេខឯកសារ ដើម្បីស្វែងរក។", "Type a document name or number to search."),
  stFilter(0, "ត្រងតាមស្ថានភាព", "Filter by status",
           "ជ្រើសរើសស្ថានភាព ដូចជា ព្រាង រង់ចាំ ឬអនុម័ត ដើម្បីបង្ហាញតែឯកសារក្នុងស្ថានភាពនោះ។",
           "Pick a status such as Draft, Pending or Approved to show only those documents."),
  stAdd("បន្ថែមឯកសារ", "Add a document", "ចុចប៊ូតុងនេះដើម្បីបង្កើតឯកសារថ្មី។", "Click this button to create a new document."),
  stTable("តារាងបង្ហាញឯកសារ ប្រភេទ ស្ថានភាព និងកាលបរិច្ឆេទផុតកំណត់។", "The table lists documents with their type, status and expiry date."),
  stActions(),
  stPaging(),
  stForm("ជ្រើសរើសប្រភេទឯកសារ ហើយបញ្ចូលឈ្មោះឯកសារ។ លេខឯកសារ និងកាលបរិច្ឆេទផុតកំណត់អាចទុកទទេបាន។",
         "Choose the document type and enter the document name. The number and expiry date are optional."),
  stSave(),
];

const DOC_TYPES = settingsCrud(
  "បញ្ចូលកូដ ឈ្មោះ និងស្ថានភាពនៃប្រភេទឯកសារ។", "Enter the code, name and status of the document type.",
  "តារាងបង្ហាញប្រភេទឯកសារទាំងអស់ដែលអាចជ្រើសរើសពេលបង្កើតឯកសារ។", "The table lists every document type that can be chosen when creating a document."
);

const DOC_APPROVAL = [
  stFilter(0, "ត្រងតាមស្ថានភាព", "Filter by status",
           "ជ្រើសរើសស្ថានភាពដើម្បីបង្ហាញតែសំណើដែលរង់ចាំ អនុម័ត ឬបដិសេធ។", "Pick a status to show only pending, approved or rejected requests."),
  stAdd("ស្នើសុំការអនុម័ត", "Request approval",
        "ចុចដើម្បីស្នើសុំឲ្យឯកសារមួយត្រូវបានពិនិត្យ និងអនុម័ត។", "Click to ask for a document to be reviewed and approved."),
  stTable("តារាងបង្ហាញសំណើទាំងអស់ រួមជាមួយឯកសារ អ្នកស្នើ និងស្ថានភាព។", "The table lists every request with its document, requester and status."),
  stActions("ចុចរូបភ្នែកដើម្បីមើលលម្អិត។ អ្នកដែលមានសិទ្ធិពិនិត្យអាចអនុម័ត ឬបដិសេធ ហើយត្រូវសរសេរមូលហេតុនៅពេលបដិសេធ។",
            "Click the eye to see the details. Reviewers can approve or reject, and must write a reason when rejecting."),
  stPaging(),
  stForm("ជ្រើសរើសឯកសារដែលចង់ស្នើសុំ ហើយបន្ថែមចំណាំប្រសិនបើចាំបាច់។", "Choose the document to request approval for, and add a note if needed."),
  stSave(),
];

const MEETING_LIST = [
  stSearch("វាយចំណងជើងកិច្ចប្រជុំដើម្បីស្វែងរក។", "Type a meeting title to search."),
  stFilter(0),
  stAdd("បន្ថែមកិច្ចប្រជុំ", "Add a meeting", "ចុចប៊ូតុងនេះដើម្បីបង្កើតកិច្ចប្រជុំថ្មី។", "Click this button to create a new meeting."),
  stPaging(),
  stForm("ជ្រើសរើសបន្ទប់ និងម៉ោងដោយចុច ឬអូសលើតារាងបន្ទប់ទំនេរជាមុនសិន បន្ទាប់មកបំពេញចំណងជើង និងប្រភេទកិច្ចប្រជុំ។ អ្នកអាចអញ្ជើញអ្នករួមចំណែកនៅទីនេះផងដែរ។",
         "First click or drag on the free-room grid to pick a room and time, then fill in the title and meeting type. You can also invite attendees here."),
  stSave(),
];

const MEETING_ROOMS = [
  stSearch("វាយលេខ ឬទីតាំងបន្ទប់ដើម្បីស្វែងរក។", "Type a room code or location to search."),
  stFilter(0, "ត្រងតាមស្ថានភាព", "Filter by status",
           "ជ្រើសរើសស្ថានភាព ដូចជា ទំនេរ ឬកំពុងជួសជុល ដើម្បីបង្ហាញតែបន្ទប់ក្នុងស្ថានភាពនោះ។",
           "Pick a status such as Available or Maintenance to show only those rooms."),
  stAdd("បន្ថែមបន្ទប់", "Add a room", "ចុចប៊ូតុងនេះដើម្បីបង្កើតបន្ទប់ប្រជុំថ្មី។", "Click this button to create a new meeting room."),
  stTable("តារាងបង្ហាញបន្ទប់ប្រជុំ ទីតាំង ចំណុះ និងស្ថានភាព។", "The table lists meeting rooms with their location, capacity and status."),
  stActions(),
  stPaging(),
  stForm("បញ្ចូលលេខកូដបន្ទប់ ទីតាំង ចំណុះ និងស្ថានភាព។ អ្នកអាចបញ្ចូលរូបភាពបន្ទប់ផងដែរ។",
         "Enter the room code, location, capacity and status. You can also add a photo of the room."),
  stSave(),
];

const DEPARTMENTS = settingsCrud(
  "បញ្ចូលកូដ ឈ្មោះ និងស្ថានភាពនាយកដ្ឋាន។", "Enter the department's code, name and status.",
  "នាយកដ្ឋានមួយមានការិយាល័យ និងតំណែងជាច្រើន ដែលត្រូវបង្កើតនៅម៉ឺនុយការិយាល័យ និងតំណែង។",
  "A department has many offices and positions, which you create under the Offices and Positions menus."
);

const OFFICES = settingsCrud(
  "ជ្រើសរើសនាយកដ្ឋានដែលការិយាល័យនេះជាកម្មសិទ្ធិ បន្ទាប់មកបញ្ចូលកូដ និងឈ្មោះការិយាល័យ។",
  "Choose the department this office belongs to, then enter the office code and name.",
  "ការិយាល័យនីមួយៗជាកម្មសិទ្ធិរបស់នាយកដ្ឋានមួយ។ មន្ត្រីត្រូវជ្រើសរើសការិយាល័យពីនាយកដ្ឋានរបស់ខ្លួន។",
  "Each office belongs to one department. An officer picks an office from their own department."
);

const POSITIONS = settingsCrud(
  "ជ្រើសរើសនាយកដ្ឋានដែលតំណែងនេះជាកម្មសិទ្ធិ បន្ទាប់មកបញ្ចូលកូដ និងឈ្មោះតំណែង។",
  "Choose the department this position belongs to, then enter the position code and name.",
  "តំណែងនីមួយៗជាកម្មសិទ្ធិរបស់នាយកដ្ឋានមួយ។ មន្ត្រីត្រូវជ្រើសរើសតំណែងពីនាយកដ្ឋានរបស់ខ្លួន។",
  "Each position belongs to one department. An officer picks a position from their own department."
);

const DEPT_MANAGERS = [
  { target: () => rootEl()?.firstElementChild?.firstElementChild || null, optional: true,
    title: tr("ជ្រើសរើសនាយកដ្ឋាន", "Choose a department"),
    text: tr("ជ្រើសរើសនាយកដ្ឋានដើម្បីមើល និងគ្រប់គ្រងអ្នកគ្រប់គ្រងរបស់វា។",
             "Choose a department to see and manage its managers.") },
  stAdd("បន្ថែមអ្នកគ្រប់គ្រង", "Add a manager",
        "ចុចដើម្បីតែងតាំងអ្នកប្រើប្រាស់ម្នាក់ជាអ្នកគ្រប់គ្រងនាយកដ្ឋាននេះ។ អ្នកអាចកំណត់ម្នាក់ជាអ្នកគ្រប់គ្រងចម្បង។",
        "Click to appoint a user as a manager of this department. You can mark one as the primary manager."),
  stTable("តារាងបង្ហាញអ្នកគ្រប់គ្រងបច្ចុប្បន្ននៃនាយកដ្ឋាន។", "The table shows the department's current managers."),
];

const USERS = [
  stSearch("វាយឈ្មោះ ឬអ៊ីមែល ដើម្បីស្វែងរកអ្នកប្រើប្រាស់។", "Type a name or email to find a user."),
  stFilter(0, "ត្រងតាមតួនាទី", "Filter by role", "ជ្រើសរើសតួនាទីដើម្បីបង្ហាញតែអ្នកប្រើប្រាស់ដែលមានតួនាទីនោះ។", "Pick a role to show only users with that role."),
  stFilter(1, "ត្រងតាមស្ថានភាព", "Filter by status", "ជ្រើសរើសស្ថានភាពគណនី ដូចជា សកម្ម ឬត្រូវបានបិទ។", "Pick an account status such as Active or Blocked."),
  stAdd("បន្ថែមអ្នកប្រើប្រាស់", "Add a user", "ចុចប៊ូតុងនេះដើម្បីបង្កើតគណនីចូលប្រើប្រាស់ថ្មី។", "Click this button to create a new login account."),
  stTable("តារាងបង្ហាញអ្នកប្រើប្រាស់ អ៊ីមែល តួនាទី ស្ថានភាព និងពេលចូលចុងក្រោយ។", "The table lists users with their email, role, status and last login."),
  stActions("ចុចរូបភ្នែកដើម្បីមើលលម្អិត។ ក្នុងផ្ទាំងលម្អិត អ្នកអាចកំណត់ពាក្យសម្ងាត់ឡើងវិញ បិទ ឬបើកគណនី។",
            "Click the eye to view details. In the details window you can reset the password, or block / activate the account."),
  stPaging(),
  stForm("ជ្រើសរើសតួនាទី និងភ្ជាប់គណនីនេះទៅមន្ត្រី។ មិនចាំបាច់បញ្ចូលពាក្យសម្ងាត់ទេ ព្រោះប្រព័ន្ធកំណត់ពាក្យសម្ងាត់លំនាំដើមឲ្យ ហើយអ្នកប្រើត្រូវប្តូរវានៅពេលចូលលើកដំបូង។",
         "Choose a role and link the account to an officer. There is no password to enter: the system sets a default one and the user must change it at first login."),
  stSave(),
];

const ROLES = [
  stAdd("បន្ថែមតួនាទី", "Add a role", "ចុចដើម្បីបង្កើតតួនាទីថ្មី ហើយជ្រើសរើសសិទ្ធិដែលតួនាទីនោះអាចប្រើ។",
        "Click to create a new role and choose which permissions it has."),
  stSearch(),
  stTable("តារាងបង្ហាញតួនាទីទាំងអស់។ តួនាទីកំណត់ថាអ្នកប្រើអាចឃើញ និងធ្វើអ្វីខ្លះ។",
          "The table lists every role. A role decides what a user can see and do."),
  stActions("ចុចរូបភ្នែកដើម្បីមើលលម្អិត និងកែសិទ្ធិរបស់តួនាទី។ ប៊ូតុងដែលអ្នកគ្មានសិទ្ធិប្រើនឹងមិនបង្ហាញទេ។",
            "Click the eye to see a role's details and change its permissions. Buttons you are not allowed to use are hidden."),
  stPaging(),
  stForm("បញ្ចូលឈ្មោះតួនាទី ឈ្មោះបង្ហាញ និងជ្រើសរើសសិទ្ធិយ៉ាងតិចមួយ។", "Enter the role name and display name, and choose at least one permission."),
  stSave(),
];

const ANN_LIST = [
  stFilter(0, "ត្រងតាមស្ថានភាព", "Filter by status", "ជ្រើសរើសស្ថានភាព ដូចជា ព្រាង ឬផ្សព្វផ្សាយ ដើម្បីបង្ហាញតែការប្រកាសក្នុងស្ថានភាពនោះ។",
           "Pick a status such as Draft or Published to show only those announcements."),
  stFilter(1, "ត្រងតាមអាទិភាព", "Filter by priority", "ជ្រើសរើសអាទិភាព ដើម្បីបង្ហាញតែការប្រកាសដែលសំខាន់ជាងគេ។", "Pick a priority to show only the most important announcements."),
  stAdd("បន្ថែមការប្រកាស", "Add an announcement", "ចុចប៊ូតុងនេះដើម្បីសរសេរការប្រកាសថ្មី។", "Click this button to write a new announcement."),
  stTable("តារាងបង្ហាញការប្រកាស អាទិភាព និងស្ថានភាព។", "The table lists announcements with their priority and status."),
  stActions(),
  stPaging(),
  stForm("បញ្ចូលចំណងជើង ខ្លឹមសារ អាទិភាព និងស្ថានភាព។ ជ្រើសរើសស្ថានភាពផ្សព្វផ្សាយ នៅពេលអ្នកចង់ឲ្យអ្នកផ្សេងឃើញ។",
         "Enter the title, content, priority and status. Choose the published status when you want others to see it."),
  stSave(),
];

const ANN_RECIPIENTS = [
  { target: selectN(0), optional: true, title: tr("ជ្រើសរើសការប្រកាស", "Choose an announcement"),
    text: tr("ជ្រើសរើសការប្រកាសដើម្បីមើលថាអ្នកណាបានអាន ឬមិនទាន់អាន។", "Choose an announcement to see who has read it and who has not.") },
  stTable("តារាងបង្ហាញអ្នកទទួលការប្រកាស និងស្ថានភាពអានរបស់ពួកគេ។", "The table shows the recipients and whether each has read it."),
  stPaging(),
];

const NOTIFICATIONS = [
  { target: () => rootEl()?.firstElementChild?.firstElementChild || null, optional: true,
    title: tr("សង្ខេប", "Summary"),
    text: tr("ចុចលើប័ណ្ណដើម្បីបង្ហាញតែការជូនដំណឹងទាំងអស់ មិនទាន់អាន ឬអានរួច។",
             "Click a card to show all, unread or read notifications only.") },
  stSearch("វាយចំណងជើង ឬសារដើម្បីស្វែងរក។", "Type a title or message to search."),
  { target: buttonWith("អានទាំងអស់", "Mark all", "Read all"), optional: true, title: tr("អានទាំងអស់", "Mark all as read"),
    text: tr("ចុចដើម្បីសម្គាល់ការជូនដំណឹងទាំងអស់ថាបានអានរួច។ ប៊ូតុងនេះបង្ហាញតែនៅពេលមានការជូនដំណឹងមិនទាន់អាន។",
             "Click to mark every notification as read. This button only shows when something is unread.") },
  stAdd("បង្កើត និងផ្ញើ", "Create and send", "ចុចដើម្បីផ្ញើការជូនដំណឹងទៅអ្នកប្រើប្រាស់ម្នាក់ ឬច្រើននាក់។",
        "Click to send a notification to one or more users.", buttonWith("ផ្ញើ", "send", "Send")),
  stTable("តារាងបង្ហាញការជូនដំណឹងរបស់អ្នក។ ការជូនដំណឹងដែលមិនទាន់អានមានសញ្ញាសម្គាល់។", "The table lists your notifications. Unread ones are marked."),
  stPaging(),
  stForm("ជ្រើសរើសប្រភេទ បញ្ចូលចំណងជើង និងសារ ហើយជ្រើសរើសអ្នកទទួល។", "Choose the type, enter a title and message, and pick the recipients.",
         buttonWith("ផ្ញើ", "send", "Send")),
  stSave(),
];

const REPORTS = [
  { target: () => rootEl()?.firstElementChild || null, title: tr("ជ្រើសរើសរបាយការណ៍", "Choose a report"),
    text: tr("ជ្រើសរើសប្រភេទរបាយការណ៍ដែលអ្នកចង់ទាញយក ដូចជា មន្ត្រី ឯកសារ ឬកិច្ចប្រជុំ។",
             "Choose the kind of report you want, such as officers, documents or meetings.") },
  { title: tr("ត្រង និងទាញយក", "Filter and export"),
    text: tr("បន្ទាប់ពីជ្រើសរើស សូមកំណត់តម្រង (ទុកទទេដើម្បីយកទាំងអស់) ហើយមើលជាមុន ឬទាញយកជាឯកសារ Excel។",
             "After choosing, set the filters (leave empty for everything), then preview the data or download it as an Excel file.") },
];

const ACTIVITY_LOG = [
  stSearch("វាយឈ្មោះអ្នកប្រើ ឬប្រភេទទិន្នន័យ ដើម្បីស្វែងរក។", "Type a user name or entity type to search."),
  stFilter(0, "ត្រងតាមសកម្មភាព", "Filter by action", "ជ្រើសរើសសកម្មភាព ដូចជា បង្កើត កែប្រែ ឬលុប។", "Pick an action such as create, update or delete."),
  stFilter(1, "ត្រងតាមប្រភេទទិន្នន័យ", "Filter by entity", "ជ្រើសរើសប្រភេទទិន្នន័យ ដើម្បីមើលតែសកម្មភាពលើទិន្នន័យនោះ។", "Pick a type of record to see only the actions on it."),
  stTable("តារាងកត់ត្រាថាអ្នកណាបានធ្វើអ្វី នៅពេលណា។ ទិន្នន័យនេះមិនអាចកែប្រែបានទេ។", "The table records who did what and when. It cannot be edited."),
  stActions("ចុចរូបភ្នែកដើម្បីមើលព័ត៌មានលម្អិតនៃសកម្មភាពនោះ។", "Click the eye to see the details of that action."),
  stPaging(),
];

export const GUIDES = {
  dashboard:              DASHBOARD,
  staff:                  STAFF,
  contract:               CONTRACT,
  "doc-officers":         DOC_OFFICERS,
  "doc-types":            DOC_TYPES,
  "doc-approval":         DOC_APPROVAL,
  "meeting-list":         MEETING_LIST,
  "meeting-rooms":        MEETING_ROOMS,
  settings:               DEPARTMENTS,
  "setting-departments":  DEPARTMENTS,
  "setting-dept-managers":DEPT_MANAGERS,
  "setting-offices":      OFFICES,
  "setting-positions":    POSITIONS,
  "setting-users":        USERS,
  "setting-roles":        ROLES,
  announcements:          ANN_LIST,
  "ann-list":             ANN_LIST,
  "ann-recipients":       ANN_RECIPIENTS,
  notifications:          NOTIFICATIONS,
  reports:                REPORTS,
  "activity-log":         ACTIVITY_LOG,
};
